/**
 * IndicatorsGrid — grade de todos os indicadores com origem de dado.
 *
 * Exibe indicadores agrupados por status: atencao → verificar → saudavel.
 * Cada linha: rótulo, valor, chip de fonte, sparkline (se series), variação,
 * status colorido, link para chat.
 *
 * Regra de ouro: nenhum número fabricado.
 * - series null → não renderiza sparkline.
 * - deltaPct null → não renderiza variação.
 */

import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { Sparkline } from './Sparkline';

interface IndicatorItem {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly group: 'atencao' | 'verificar' | 'saudavel';
  readonly source: string;
  readonly series: readonly number[] | null;
  readonly deltaPct: number | null;
}

interface IndicatorsGridProps {
  readonly items: readonly IndicatorItem[];
}

function GroupLabel({
  group,
}: {
  readonly group: 'atencao' | 'verificar' | 'saudavel';
}): JSX.Element {
  const label =
    group === 'atencao'
      ? t('radar.indicatorsGroupAtencao')
      : group === 'verificar'
        ? t('radar.indicatorsGroupVerificar')
        : t('radar.indicatorsGroupSaudavel');
  return <div className="ind-group-label">{label}</div>;
}

function StatusChip({
  group,
}: {
  readonly group: 'atencao' | 'verificar' | 'saudavel';
}): JSX.Element {
  if (group === 'atencao') {
    return (
      <span className="ind-status ind-status-atencao">
        <span
          className="material-symbols-rounded"
          aria-hidden="true"
          style={{ fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20" }}
        >
          warning
        </span>
        {t('radar.indicatorsGroupAtencao')}
      </span>
    );
  }
  if (group === 'verificar') {
    return (
      <span className="ind-status ind-status-verificar">
        <span
          className="material-symbols-rounded"
          aria-hidden="true"
          style={{ fontVariationSettings: "'FILL' 0,'wght' 600,'GRAD' 0,'opsz' 20" }}
        >
          help
        </span>
        {t('radar.indicatorsGroupVerificar')}
      </span>
    );
  }
  return (
    <span className="ind-status ind-status-saudavel">
      <span
        className="material-symbols-rounded"
        aria-hidden="true"
        style={{ fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20" }}
      >
        check_circle
      </span>
      {t('radar.indicatorsGroupSaudavel')}
    </span>
  );
}

function DeltaDisplay({ deltaPct }: { readonly deltaPct: number }): JSX.Element {
  const isUp = deltaPct > 0;
  const isDown = deltaPct < 0;
  const arrow = isUp ? '▲' : isDown ? '▼' : '—';
  const cls = isUp
    ? 'ind-trend ind-trend-up'
    : isDown
      ? 'ind-trend ind-trend-down'
      : 'ind-trend ind-trend-flat';
  return (
    <span className={cls}>
      {arrow} {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
}

function IndicatorRow({
  item,
  onOpenChat,
}: {
  readonly item: IndicatorItem;
  readonly onOpenChat: (id: string) => void;
}): JSX.Element {
  const hasSeries = item.series !== null && item.series.length >= 2;

  return (
    <div
      className={`ind-row ind-row-${item.group}`}
      role="button"
      tabIndex={0}
      aria-label={`${item.label}: ${item.value}. Clique para perguntar a CROSS.`}
      onClick={() => onOpenChat(item.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenChat(item.id);
        }
      }}
      style={{ animationDelay: '0ms' }}
    >
      {/* Nome + sub */}
      <div className="ind-name">{item.label}</div>

      {/* Valor */}
      <div className="ind-value">{item.value}</div>

      {/* Chip de fonte */}
      <div>
        <span className="ind-source-chip">
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20" }}
          >
            hub
          </span>
          {item.source}
        </span>
      </div>

      {/* Sparkline — só se series != null */}
      <div className="ind-spark-col">
        {hasSeries ? (
          <Sparkline
            points={item.series as number[]}
            width={100}
            height={32}
            ariaLabel={`Tendencia de ${item.label}`}
          />
        ) : null}
        {item.deltaPct !== null ? <DeltaDisplay deltaPct={item.deltaPct} /> : null}
      </div>

      {/* Status colorido */}
      <StatusChip group={item.group} />

      {/* Link chat */}
      <div>
        <span className="ind-origin-link" aria-hidden="true">
          <span
            className="material-symbols-rounded"
            style={{ fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20" }}
          >
            forum
          </span>
          {t('radar.indicatorOpenChat')}
        </span>
      </div>
    </div>
  );
}

export function IndicatorsGrid({ items }: IndicatorsGridProps): JSX.Element {
  const navigate = useNavigate();

  // Protege contra undefined quando a API retorna shape incompleto (ex.: HTTP 429)
  const safeItems = items ?? [];

  const openChat = (id: string): void => {
    navigate(`/chat?context=indicator:${id}`);
  };

  if (safeItems.length === 0) {
    return <p className="muted radar-indicators-empty">{t('radar.indicatorsEmpty')}</p>;
  }

  const groups: Array<'atencao' | 'verificar' | 'saudavel'> = ['atencao', 'verificar', 'saudavel'];

  return (
    <div className="ind-grid" role="list">
      {groups.map((group) => {
        const groupItems = safeItems.filter((i) => i.group === group);
        if (groupItems.length === 0) return null;
        return (
          <div key={group} role="group" aria-label={group}>
            <GroupLabel group={group} />
            {groupItems.map((item) => (
              <div key={item.id} role="listitem" style={{ marginBottom: 6 }}>
                <IndicatorRow item={item} onOpenChat={openChat} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
